/**
 * FlowGraphSubgraphBlock — a FlowGraph execution block that delegates to an inner graph.
 *
 * Builds on Babylon.js FlowGraph primitives (FlowGraphExecutionBlock, FlowGraphContext)
 * to support compound subgraph nodes in the Trace behavior graph compiler.
 */

import type { IFlowGraphBlockConfiguration } from "@babylonjs/core/FlowGraph/flowGraphBlock";
import type { FlowGraphBlock } from "@babylonjs/core/FlowGraph/flowGraphBlock";
import type { FlowGraphContext } from "@babylonjs/core/FlowGraph/flowGraphContext";
import { FlowGraphExecutionBlock } from "@babylonjs/core/FlowGraph/flowGraphExecutionBlock";
import type { FlowGraphSignalConnection } from "@babylonjs/core/FlowGraph/flowGraphSignalConnection";
import { RichTypeAny } from "@babylonjs/core/FlowGraph/flowGraphRichTypes";
import type { FlowGraphDataConnection } from "@babylonjs/core/FlowGraph/flowGraphDataConnection";
import type { FlowGraph } from "@babylonjs/core/FlowGraph/flowGraph";

export interface IFlowGraphSubgraphInterface {
    inputFlowSockets: Map<string, { block: FlowGraphExecutionBlock; signalName: string }>;
    outputFlowSockets: Map<string, { block: FlowGraphExecutionBlock; signalName: string }>;
    inputValueSockets: Map<string, { connections: Array<{ block: FlowGraphBlock; inputName: string }>; variableName?: string }>;
    outputValueSockets: Map<string, { block: FlowGraphBlock; outputName: string }>;
}

export interface IFlowGraphSubgraphBlockConfiguration extends IFlowGraphBlockConfiguration {
    subgraphInterface: IFlowGraphSubgraphInterface;
    innerGraph: FlowGraph;
    /** Optional initializer applied to each new inner context (sets variable defaults, inline values) */
    initContext?: (ctx: FlowGraphContext) => void;
}

const SUBGRAPH_EXIT_VAR = "__flowgraph_subgraph_exit";

export class FlowGraphSubgraphBlock extends FlowGraphExecutionBlock {
    private _interface: IFlowGraphSubgraphInterface;
    private _innerGraph: FlowGraph;
    private _initContext?: (ctx: FlowGraphContext) => void;
    private _innerContexts = new WeakMap<FlowGraphContext, FlowGraphContext>();
    private _outputFlowSignals = new Map<string, FlowGraphSignalConnection>();
    private _inputDataConnections = new Map<string, FlowGraphDataConnection<unknown>>();
    private _outputDataInfo = new Map<string, {
        connection: FlowGraphDataConnection<unknown>;
        innerBlock: FlowGraphBlock;
        innerOutputName: string;
    }>();

    constructor(config: IFlowGraphSubgraphBlockConfiguration) {
        super(config);
        this._interface = config.subgraphInterface;
        this._innerGraph = config.innerGraph;
        this._initContext = config.initContext;

        for (const [name] of this._interface.inputFlowSockets) {
            if (name !== "in") {
                this._registerSignalInput(name);
            }
        }
        for (const [name] of this._interface.outputFlowSockets) {
            this._outputFlowSignals.set(name, this._registerSignalOutput(name));
        }
        for (const [name] of this._interface.inputValueSockets) {
            this._inputDataConnections.set(name, this.registerDataInput(name, RichTypeAny));
        }
        for (const [name, ref] of this._interface.outputValueSockets) {
            const conn = this.registerDataOutput(name, RichTypeAny);
            this._outputDataInfo.set(name, { connection: conn, innerBlock: ref.block, innerOutputName: ref.outputName });
        }
    }

    public get innerGraph(): FlowGraph { return this._innerGraph; }
    public get subgraphInterface(): IFlowGraphSubgraphInterface { return this._interface; }

    private _getInnerContext(outerContext: FlowGraphContext): FlowGraphContext {
        let inner = this._innerContexts.get(outerContext);
        if (!inner) {
            inner = this._innerGraph.createContext();
            if (this._initContext) this._initContext(inner);
            this._innerContexts.set(outerContext, inner);
        }
        return inner;
    }

    private _bridgeInputValues(outerContext: FlowGraphContext, innerCtx: FlowGraphContext): void {
        for (const [name, conn] of this._inputDataConnections) {
            const value = conn.getValue(outerContext);
            const socketInfo = this._interface.inputValueSockets.get(name);
            if (socketInfo) {
                for (const target of socketInfo.connections) {
                    const input = target.block.getDataInput(target.inputName);
                    if (input) input.setValue(value, innerCtx);
                }
                if (socketInfo.variableName) {
                    innerCtx.setVariable(socketInfo.variableName, value);
                }
            }
        }
    }

    public _execute(context: FlowGraphContext, callingSignal: FlowGraphSignalConnection): void {
        const innerCtx = this._getInnerContext(context);
        innerCtx.setVariable(SUBGRAPH_EXIT_VAR, "");
        this._bridgeInputValues(context, innerCtx);

        const entryInfo = this._interface.inputFlowSockets.get(callingSignal.name);
        if (!entryInfo) return;

        const entrySignal = entryInfo.block.getSignalInput(entryInfo.signalName);
        if (entrySignal) entrySignal._activateSignal(innerCtx);

        const exitName = innerCtx.getVariable(SUBGRAPH_EXIT_VAR) as string;
        if (exitName) {
            const outerSignal = this._outputFlowSignals.get(exitName);
            if (outerSignal) outerSignal._activateSignal(context);
        }
    }

    public override _updateOutputs(context: FlowGraphContext): void {
        const innerCtx = this._getInnerContext(context);
        this._bridgeInputValues(context, innerCtx);

        for (const [, info] of this._outputDataInfo) {
            const innerOutput = info.innerBlock.getDataOutput(info.innerOutputName);
            if (innerOutput) info.connection.setValue(innerOutput.getValue(innerCtx), context);
        }
    }

    public override getClassName(): string { return "FlowGraphSubgraphBlock"; }
}

/** Exit sentinel — records which output flow was reached */
export class ExitSentinelBlock extends FlowGraphExecutionBlock {
    public readonly exitName: string;
    constructor(exitName: string) {
        super({ name: `__sentinel_${exitName}` });
        this.exitName = exitName;
    }
    public _execute(context: FlowGraphContext): void {
        context.setVariable(SUBGRAPH_EXIT_VAR, this.exitName);
    }
    public override getClassName(): string { return "ExitSentinelBlock"; }
}

/** Wire exit sentinels onto a subgraph's output flow exit points. */
export function installExitSentinels(
    outputFlowBlocks: Map<string, { block: FlowGraphExecutionBlock; signalName: string }>,
): ExitSentinelBlock[] {
    const sentinels: ExitSentinelBlock[] = [];
    for (const [exitName, ref] of outputFlowBlocks) {
        const sentinel = new ExitSentinelBlock(exitName);
        const signalOut = ref.block.getSignalOutput(ref.signalName);
        if (signalOut) signalOut.connectTo(sentinel.in);
        sentinels.push(sentinel);
    }
    return sentinels;
}
